/*

Example of a GBDS disclosure w/ VDC data that's conditional based on selected region:

	<span
		class="gb-price-disclosure"
		data-quebec-as-shown-disclosure="50,000"
		data-quebec-disclosure='<gb-disclosure class="gb-disclosure" role="button" data-disclosure-id="/content/chevrolet/na/us/en/common-disclosures-library/msrp"><sup><span role="button" class="show-disclosure q-mod q-mod-rte-disclosure q-rte-disclosure" tabindex="0" data-disclosure-path="/content/chevrolet/na/us/en/common-disclosures-library/msrp">†</span></sup> &nbsp;</gb-disclosure>'
		data-others-disclosure='<gb-disclosure class="gb-disclosure" role="button" data-disclosure-id="/content/chevrolet/na/us/en/common-disclosures-library/msrp1234"><sup><span role="button" class="show-disclosure q-mod q-mod-rte-disclosure q-rte-disclosure" tabindex="0" data-disclosure-path="/content/chevrolet/na/us/en/common-disclosures-library/msrp1234">†</span></sup> &nbsp;</gb-disclosure>'
	>
		<gb-disclosure
			class="gb-disclosure"
			role="button"
			data-disclosure-id="/content/chevrolet/na/us/en/common-disclosures-library/msrp"
		>
			<sup>
				<span
					role="button"
					class="show-disclosure q-mod q-mod-rte-disclosure q-rte-disclosure"
					tabindex="0"
					data-disclosure-path="/content/chevrolet/na/us/en/common-disclosures-library/msrp"
				>
					<span class="q-disclosure-print-hide">
						†
					</span>
					<span class="q-disclosure-print-number">(undefined)</span>
				</span>
			</sup>
		</gb-disclosure>
	</span>


Example of regional pricing text w/ VDC data (or overwritten authored data):

	<span class="q-price dynamic-price" data-others-msrp="$38,198" data-quebec-msrp="$40,348">$38,198</span>

*/

import { GlobalNav } from '../components/global-nav/global-nav'

type Container = HTMLElement | Element | ShadowRoot | Document

const regionalDefaultAttributePrefix = 'data-others'
const disclosureAttribute = `${regionalDefaultAttributePrefix}-disclosure`
const textAttribute = `${regionalDefaultAttributePrefix}-msrp`
const regionalDisclosureSelector = `[${disclosureAttribute}]`
const regionalTextSelector = `[${textAttribute}]`
const combinedRegionalContentSelector = `${regionalDisclosureSelector}, ${regionalTextSelector}`

/**
 * Just a simple wrapping of `for() {}`; slightly more performative than `forEach` (contextually).
 * @param array Array to iterate over.
 * @param callback Callback for each array item to execute.
 */
function iterate<T>(array: T[], callback: (arrayItem: T) => void) {
	for (let i = 0; i < array.length; i++) {
		const item = array[i]
		callback(item)
	}
}

/**
 * Finds elements that have attributes that contain a string.
 * @param container container to `querySelector` from.
 * @param selector CSS selector to query for elements.
 * @param attributeStrings the string(s) to search for and check if any element's attributes contain.
 * @example 'data-others-'
 * @returns an array of matching elements.
 */
function getElementsWithAttributeStrings(
	container: Container,
	selector: string,
	...attributeStrings: string[]
): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(e =>
		attributeStrings.some(attributeString => e.hasAttribute(attributeString))
	)
}

/**
 * Parse a string as DOM and insert it into a container, replacing existing content.
 * @param container container to insert content into.
 * @param content string of HTML to insert.
 */
function replaceInnerContent(container: Container, content: string) {
	const domContent = new DOMParser().parseFromString(content, 'text/html')

	Array.from(container.children).forEach(n => n.remove())
	domContent.body.childNodes.forEach(n => container.appendChild(n))
}

/**
 * Replace an element's textContent.
 * @param container container to insert content into.
 * @param text string of text to replace.
 */
function replaceTextContent(container: Container, text: string) {
	container.textContent = text
}

/**
 * Replaces the content of elements with region-specific content based on data attributes, e.g. `data-quebec-disclosure`, etc.
 * @param array array of elements to replace HTML content for.
 * @param region region to set content for.
 */
function replaceWithRegionalInnerContent(array: HTMLElement[], region: string) {
	iterate(array, e => {
		const regionDisclosureAttributeMatch = findAttributeMatch(
			e,
			a => a.name.toLowerCase() === `data-${region.toLowerCase()}-disclosure`
		)
		const regionTextAttributeMatch = findAttributeMatch(
			e,
			a => a.name.toLowerCase() === `data-${region.toLowerCase()}-msrp`
		)

		// The conditions are mutually exclusive; it's easier to just call this for both types of potential matches due to
		// otherwise confusing conditional logic.
		replaceRegionalMatchedAttributeContent(
			e,
			regionDisclosureAttributeMatch,
			regionTextAttributeMatch,
			`${regionalDefaultAttributePrefix}-disclosure`,
			replaceInnerContent
		)
		replaceRegionalMatchedAttributeContent(
			e,
			regionTextAttributeMatch,
			regionDisclosureAttributeMatch,
			`${regionalDefaultAttributePrefix}-msrp`,
			replaceTextContent
		)
	})
}

/**
 * Returns an attribute on an element that matches the provided callback logic, can return undefined if no match found.
 * @param element The element to find an attribute match for.
 * @param callback Callback to perform that determines the attribute match.
 */
function findAttributeMatch(element: Element, callback: (args: Attr) => boolean) {
	return Array.from(element.attributes).find(a => callback(a))
}

/**
 * Generic function to replace a regional element's contents with the provided callback depending on if the provided attribute is defined.
 * @param element Element to replace content for.
 * @param attribute Attribute to check for and use; can be undefined.
 * @param attributeYouDontWant Attribute you want to make sure the element *doesn't* have; can be undefined.
 * @param defaultAttribute string of the default attribute to look for in the case none of the conditionals are true.
 * @param callback Callback to use for replacing the element's contents.
 */
function replaceRegionalMatchedAttributeContent(
	element: HTMLElement,
	attribute: Attr,
	attributeYouDontWant: Attr,
	defaultAttribute: string,
	callback: (...args: unknown[]) => void
) {
	if (attribute && !attributeYouDontWant) {
		callback(element, attribute.value)
	} else {
		const defaultMatch = findAttributeMatch(element, a => a.name.toLowerCase() === defaultAttribute.toLowerCase())
		if (defaultMatch) callback(element, defaultMatch.value)
	}
}

/**
 * Subscribes to region changes and replaces HTML content with region appropriate content within the navigation.
 */
export async function initRegionalContent(): Promise<void> {
	const nav = document.querySelector('gb-global-nav') as GlobalNav

	await nav.flyoutService.allFlyoutsLoaded
	window.gbpe.regionService.currentRegion$.subscribe(region => {
		// Elements with 'data-others-' are those that come from the VDC and have 'default' content
		// that is called 'others'. So when the current region doesn't match region specific content,
		// 'others' content is displayed instead.

		// Replacing regional disclosures and regional text with this
		const navRegionalDisclosure = getElementsWithAttributeStrings(
			nav.shadowRoot,
			combinedRegionalContentSelector,
			disclosureAttribute,
			textAttribute
		)
		replaceWithRegionalInnerContent(navRegionalDisclosure, region.name)

		/* This might come back in the future but for now we don't need it.
				const outsideNavRegionalContent = getElementsWithAttributeStrings(
					document.body,
					combinedRegionalContentSelector,
					disclosureAttribute,
					textAttribute
				)
				replaceWithRegionalInnerContent(outsideNavRegionalContent, region.name)
			*/
	})
}
